'use client';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  CategoryCard,
  CategoryCardImage,
  CategoryCardName,
} from '@eliadmin/core/src/components/categories/category-card';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { CATEGORY_CONFIG } from '@eliadmin/core/src/constants/category-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import React, { useState } from 'react';
import { FiChevronLeft, FiHome } from 'react-icons/fi';

const Breadcrumbs = ({ categoryStack, onBreadcrumbClick }: any) => {
  if (!categoryStack.length) return;

  return (
    <nav className="breadcrumb px-3 sm:px-0">
      <ul className="flex items-center space-x-2 py-2">
        <li>
          <p
            className="cursor-pointer text-primary hover:underline"
            onClick={() => onBreadcrumbClick(0)}
          >
            <FiHome />
          </p>
        </li>
        {categoryStack.map((category: CategoriesEntity, index: number) => (
          <li key={category.slug} className="flex items-center">
            <span className="ml-1 mr-2 cursor-default text-primary">/</span>
            <p
              className={cn('line-clamp-1 cursor-pointer text-primary', {
                'cursor-default text-foreground': index === categoryStack.length - 1,
                'hover:underline': index !== categoryStack.length - 1,
              })}
              onClick={() => onBreadcrumbClick(index + 1)}
            >
              {category.name}
            </p>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export const CategoriesPageNew = ({ categories }: any) => {
  const [categoryStack, setCategoryStack] = useState<any>([]);

  const handleCategoryClick = (category: any) => {
    setCategoryStack((prevStack: any) => [...prevStack, category]);
    window.scrollTo(0, 0);
  };

  const handleBackClick = () => {
    setCategoryStack((prevStack: any) => prevStack.slice(0, -1));
    window.scrollTo(0, 0);
  };

  const handleBreadcrumbClick = (index: number) => {
    setCategoryStack(categoryStack.slice(0, index));
    window.scrollTo(0, 0);
  };

  const currentCategory = categoryStack.length > 0 ? categoryStack[categoryStack.length - 1] : null;
  const categoriesToDisplay = currentCategory ? currentCategory.childs : categories.data;

  return (
    <Container className="px-0 py-4 sm:px-4">
      <Breadcrumbs categoryStack={categoryStack} onBreadcrumbClick={handleBreadcrumbClick} />
      <div className="flex w-full flex-col px-2 sm:w-auto sm:px-0">
        <div className="flex items-center">
          {categoryStack.length > 0 && (
            <p className="min-w-7">
              <FiChevronLeft onClick={handleBackClick} className="h-6 w-6 text-primary" />
            </p>
          )}
          <p className="ml-2 text-2xl font-bold ">
            {categoryStack.length > 0 ? categoryStack[categoryStack.length - 1].name : 'Kategórie'}
          </p>
        </div>
        {categoryStack.length > 0 && (
          <Link
            href={categoryStack[categoryStack.length - 1].slug}
            className="pl-2 pt-4 text-primary hover:underline"
          >
            Zobraziť všetko v podkategórii
          </Link>
        )}
      </div>
      <Container
        className={cn(
          'hidden grid-cols-1 gap-4 px-0 py-4 sm:grid sm:grid-cols-2 md:grid-cols-4',
          'ea-categories',
        )}
      >
        {categoriesToDisplay.map((category: any) => (
          <CategoryCard
            key={category.slug}
            href={`/${category.slug}`}
            className={{ root: 'min-h-[60px]', content: 'p-0 pl-2' }}
          >
            <Link
              href={`/${category.slug}` || '#'}
              className={cn('flex w-full items-center gap-2 py-2 pr-2', 'ea-category-card__link')}
              prefetch={false}
            >
              {category.image?.url ? (
                <CategoryCardImage
                  url={category.image.url}
                  alt={category.image.custom_title || category.image.name}
                />
              ) : (
                <CategoryCardImage url={'/medistore-favicon.jpg'} alt={category.name} />
              )}
              <CategoryCardName>{category.name}</CategoryCardName>
            </Link>

            {!!category.childs.length && (
              <div
                className={cn(
                  'flex h-full flex-1 cursor-pointer items-center justify-end pl-12 pr-2 text-left text-foreground hover:bg-accent/10',
                  'ea-category-card-arrow',
                )}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCategoryClick(category);
                }}
              >
                {React.cloneElement(CATEGORY_CONFIG.categoryCard.arrowIcon, {
                  className: cn('w-5 h-5 text-primary', 'ea-category-card-arrow__icon'),
                })}
              </div>
            )}
          </CategoryCard>
        ))}
      </Container>
      <Container
        className={cn(
          'grid grid-cols-1 gap-0 px-0 py-4 sm:hidden sm:grid-cols-2 md:grid-cols-4',
          'ea-categories',
        )}
      >
        {categoriesToDisplay.map((category: any, index: number) => (
          <CategoryCard
            key={category.slug}
            href={`/${category.slug}`}
            className={{
              root: cn(
                'min-h-[60px] rounded-none border-b-0 border-l-0 border-r-0',
                index === categoriesToDisplay.length - 1 ? 'border-b' : '',
              ),
              content: 'p-0 pl-2',
            }}
          >
            <Link
              href={`/${category.slug}` || '#'}
              className={cn('flex w-full items-center gap-2 py-2 pr-2', 'ea-category-card__link')}
              prefetch={false}
            >
              {category.image?.url ? (
                <CategoryCardImage
                  url={category.image.url}
                  alt={category.image.custom_title || category.image.name}
                />
              ) : (
                <CategoryCardImage url={'/medistore-favicon.jpg'} alt={category.name} />
              )}
              <CategoryCardName>{category.name}</CategoryCardName>
            </Link>

            {!!category.childs.length && (
              <div
                className={cn(
                  'flex h-full flex-1 cursor-pointer items-center justify-end pl-12 pr-2 text-left text-foreground hover:bg-accent/10',
                  'ea-category-card-arrow',
                )}
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCategoryClick(category);
                }}
              >
                {React.cloneElement(CATEGORY_CONFIG.categoryCard.arrowIcon, {
                  className: cn('w-7 h-7 text-primary', 'ea-category-card-arrow__icon'),
                })}
              </div>
            )}
          </CategoryCard>
        ))}
      </Container>
    </Container>
  );
};
